import { Routes, Route, Navigate, useLocation } from 'react-router';
import { ROUTES } from './constants/routes';
import { Suspense, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useMobileDetection } from './hooks/ui/useMobileDetection';
import { isPhoneViewport } from './utils/phoneViewport';
import { useAppToast } from './hooks/ui/useAppToast';
import { useDocumentScrollLock } from './hooks/ui/useDocumentScrollLock';
import { usePageTimeTracking } from './hooks/analytics';
import { FEATURES } from './config/env';

import Layout from './components/layout/Layout';
import {
  WorkspaceProvider,
  useWorkspaceId,
  useWorkspaceInitializing,
} from './contexts/WorkspaceContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AgentChatProvider } from './contexts/AgentChatProvider';
import { ChatContextProvider } from './contexts/ChatContextProvider';
import { SocketProvider } from './contexts/SocketContext';
import { UIProvider } from './contexts/UIContext';
import { useNotificationSocket } from './hooks/ui/useNotificationSocket';
import { useBillingRealtime } from './hooks/data/useBilling';
import { useConnectorImportTraySocket } from './hooks/data/useConnectorImport';
import { ErrorBoundary, RouteErrorFallback } from './components/ErrorBoundary';
import { MobileCaptureOrRedeem } from './components/routing/MobileCaptureOrRedeem';
import { WorkspacePageForShell } from './components/routing/WorkspacePageForShell';
import {
  CaptureStreamRedirect,
  LegacyWorkspaceListRedirect,
  MobileAttachmentRedirect,
  MobileNoteRedirect,
  ProjectRedirect,
  ProjectsLegacyRedirect,
  SampleRedirect,
} from './components/routing/legacyRedirects';
import { LoadingFallback } from './components/routing/LoadingFallback';
import { WorkspaceRoute } from './components/routing/WorkspaceRoute';
import { WriterRoute } from './components/routing/WriterRoute';
import { AuthRedirect } from './components/routing/AuthRedirect';
import { usePendingInvitedWorkspaceSwitch } from './hooks/workspace/usePendingInvitedWorkspaceSwitch';
import { usePendingInvitationRefusalToast } from './hooks/workspace/usePendingInvitationRefusalToast';
import { sealGa4ForAuthenticatedSession, trackPreAuthPageView } from './utils/ga4';
import { CookieConsent } from './components/features/CookieConsent';
import { UpgradePromptModal } from './components/billing/UpgradePromptModal';
import { UploadTray } from './components/upload/UploadTray';
import { AiQuotaWarningProvider } from './providers/AiQuotaWarningProvider';
import { ViewerForbiddenToastBridge } from './components/system/ViewerForbiddenToastBridge';
import { IdleTimeoutWarning } from './components/features/IdleTimeoutWarning';
import { RecordingRecoveryPrompt } from './components/features/capture/RecordingRecoveryPrompt';
import { sweepRecordings } from './services/recording/durableRecording';
import { queryClient, clearWorkspaceQueries } from './lib/queryClient';
import { safeInternalReturnUrl } from './utils/returnUrl';
import { loadKatexStyles } from './utils/katexStyles';

// Lazy-loaded pages live in ./lazyPages so this file stays under its size gate.
import { isMobileSupportedPath } from './config/mobileRoutes';
import {
  AccountSettings,
  AttachmentDetail,
  SpreadsheetDetail,
  LinkRedirect,
  Audit,
  AuthAction,
  ConnectorOAuthCallback,
  Dashboard,
  EmailVerificationPrompt,
  EquipmentDetail,
  Graph,
  InternalAdmin,
  LiveCallPage,
  LiveSessionPage,
  Login,
  MagicLinkRedemption,
  MobileBirdPage,
  MobileFoldersWorkspacePage,
  NotFound,
  NoteDetail,
  Onboarding,
  ProtocolDetail,
  ProtocolLibraryGridPage,
  ProtocolRunPage,
  PublicSharePage,
  RecoverEmail,
  ResetPassword,
  VerifyEmail,
  TrashGridPage,
  WorkosSignIn,
  WorkspaceCanvasPage,
} from './lazyPages';
import { NoWorkspaceGate } from './components/workspace/NoWorkspaceGate';
/**
 * Navigate variant that preserves the current location.search on the redirect.
 * Used for the auth/onboarding redirect chain so utm_* params survive a click
 * from a marketing email through to the final landing page.
 */
function NavigateWithSearch({ to, replace }: { to: string; replace?: boolean }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace={replace} />;
}

/**
 * Post-login landing for /auth (non-invitation). Honours a safe `returnUrl`
 * so flows that bounce an unauthenticated user through sign-in resume where
 * they left off — notably the WorkOS MCP connect page (/auth/workos), which
 * redirects to /login?returnUrl=<itself> when the user isn't signed in.
 * Falls back to the dashboard, preserving search params (utm_* etc.).
 */
function PostLoginRedirect() {
  const location = useLocation();
  const returnUrl = safeInternalReturnUrl(location.search);
  if (returnUrl) return <Navigate to={returnUrl} replace />;
  return <Navigate to={{ pathname: ROUTES.dashboard, search: location.search }} replace />;
}

// Mobile-specific pages (mobile-only experiences that have no desktop equivalent)
// QuickCapturePage is reached through MobileCaptureOrRedeem, which owns both
// lazy chunks. Only the UNAUTHENTICATED tree binds MagicLinkRedemption
// directly — a tokenless request from a signed-out device must go to sign-in,
// never render the protected camera page.

/**
 * Main App component
 * Handles routing, authentication, and global state initialization
 */

// Paths that are supported on mobile — includes /m/ mobile-only experiences
// and unified responsive entity detail routes.
// Checked as exact match or prefix (e.g. '/notes' also covers '/notes/123').

// Protected Route component - redirects to onboarding if not onboarded, or email verification if not verified
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, bootLoading } = useAuth();
  const location = useLocation();
  const { isMobile } = useMobileDetection();

  // Wait for auth to fully load before making routing decisions
  // This prevents false redirects during page refresh when backend call is in-flight
  if (bootLoading) {
    return <LoadingFallback />;
  }

  // Check email verification first (most important security check)
  // Allow access to auth routes, verification pages, and onboarding even if not verified
  const isAuthRoute =
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname.startsWith('/reset-password') ||
    location.pathname === '/verify-email-prompt' ||
    location.pathname === '/onboarding';

  // Mirror of the backend's `requireEmailVerification` exemption
  // (packages/backend/src/middleware/auth.ts:315): accounts on the
  // `@bower-e2e.test` reserved TLD (RFC 2606) bypass the gate. The
  // E2E ephemeral-user fixture creates accounts via Firebase REST
  // `accounts:signUp` which cannot set `emailVerified: true` without
  // admin credentials, and the persistent bot pool's signups land
  // the same way. Real users can never sign up with this TLD —
  // ICANN reserves `.test` — so the exemption can't be leveraged
  // by anything customer-facing. Keep this in sync with the backend
  // check; if either side flips, the bot suite breaks.
  const isE2ETestUser = (user?.email ?? '').toLowerCase().endsWith('@bower-e2e.test');
  if (user && !user.emailVerified && !isE2ETestUser && !isAuthRoute) {
    return (
      <Navigate to={{ pathname: ROUTES.verifyEmailPrompt, search: location.search }} replace />
    );
  }

  // Only redirect to onboarding if we explicitly know user is NOT onboarded
  // user.onboarded === false means not onboarded
  // user.onboarded === undefined means still loading from backend (don't redirect)
  // user.onboarded === true means onboarded (proceed)
  if (user && user.onboarded === false) {
    return <Navigate to={{ pathname: ROUTES.onboarding, search: location.search }} replace />;
  }

  // Synchronous mobile redirect — done here rather than in a useEffect so the
  // desktop page never paints before the redirect. isMobile is initialised from
  // the user-agent synchronously, so this is correct on the very first render.
  if (isMobile && !isMobileSupportedPath(location.pathname)) {
    return <Navigate to={{ pathname: ROUTES.mobileCapture, search: location.search }} replace />;
  }

  return <>{children}</>;
}

// Mounts the notification socket listener inside SocketProvider scope
function NotificationListener() {
  useNotificationSocket();
  useBillingRealtime();
  // App-level connector-import tray feeder — must live here (not in the import
  // wizard) so per-page progress rows + their done state survive the wizard
  // closing the moment the user clicks Import.
  useConnectorImportTraySocket();
  return null;
}

// Authenticated App component - requires WorkspaceProvider to be higher in tree
function AuthenticatedApp() {
  const { user } = useAuth();
  const workspaceId = useWorkspaceId();
  const workspaceInitializing = useWorkspaceInitializing();
  const toast = useAppToast();

  // Analytics tracking hooks - installed at app root level
  usePageTimeTracking(); // Page view and time tracking
  // Lands an invitee in the workspace they just joined, whichever accept path ran.
  usePendingInvitedWorkspaceSwitch();
  // Says why the server refused a Google/Microsoft sign-in's invite link.
  usePendingInvitationRefusalToast();

  // Cross-tab synchronization: Listen for workspace changes in other tabs
  useEffect(() => {
    if (!user || !user.onboarded) {
      return;
    }

    const handleStorageChange = (event: StorageEvent) => {
      // Handle workspace ID changes from other tabs
      if (event.key === 'workspaceId' && event.newValue && event.newValue !== workspaceId) {
        // Clear TanStack Query cache for clean state
        clearWorkspaceQueries();

        // Show toast notification
        toast({
          title: 'Workspace Changed',
          description: 'Switched workspaces in another tab. Page will reload shortly.',
          status: 'info',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });

        // Page reload will ensure clean state with new workspace
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user, workspaceId, toast]);

  // Show loading spinner during workspace initialization (only for onboarded users)
  if (user?.onboarded && workspaceInitializing) {
    return <LoadingFallback />;
  }

  return (
    <ErrorBoundary level="app">
      <SocketProvider>
        <ChatContextProvider>
          <IdleTimeoutWarning />
          {/* Keyed by user: an in-place sign-in as someone else starts empty. */}
          {user && <RecordingRecoveryPrompt key={user.internalId || user.uid} />}
          <NotificationListener />
          {/* ABOVE Layout, and inside SocketProvider — both load-bearing, and
              both argued in AgentChatProvider's own docblock. */}
          <AgentChatProvider>
            <Layout>
              <NoWorkspaceGate>
                <ErrorBoundary level="route" fallback={<RouteErrorFallback />}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {/* Default route - redirect to dashboard */}
                      <Route
                        path={ROUTES.root}
                        element={
                          <ProtectedRoute>
                            <NavigateWithSearch to={ROUTES.dashboard} replace />
                          </ProtectedRoute>
                        }
                      />

                      {/* Onboarding route for authenticated users */}
                      <Route
                        path={ROUTES.onboarding}
                        element={
                          <ErrorBoundary level="route">
                            <Onboarding />
                          </ErrorBoundary>
                        }
                      />

                      {/* Mobile-specific routes */}
                      <Route
                        path={ROUTES.mobileCapture}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <MobileCaptureOrRedeem />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.mobileBird}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <MobileBirdPage />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.mobileLive}
                        element={<Navigate to={ROUTES.mobileBird} replace />}
                      />
                      <Route
                        path={ROUTES.mobileLiveSession}
                        element={<Navigate to={ROUTES.liveSession} replace />}
                      />
                      <Route
                        path={ROUTES.mobileWorkspace}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <MobileFoldersWorkspacePage />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy /m/notes redirects */}
                      <Route
                        path={ROUTES.mobileNotes}
                        element={<Navigate to={ROUTES.mobileWorkspace} replace />}
                      />
                      <Route
                        path={ROUTES.mobileNoteLegacy.pattern}
                        element={<MobileNoteRedirect />}
                      />
                      <Route
                        path={ROUTES.mobileAttachmentLegacy.pattern}
                        element={<MobileAttachmentRedirect />}
                      />
                      {/* Live session — unified route for both mobile and desktop.
                      Write-only: the agent-service handshake rejects a
                      read-only session. WorkspaceRoute is paired ahead of
                      WriterRoute per WriterRoute's own docblock — it waits
                      for the workspace (and its userRole) to load before
                      the read-only check fires, so a guest deep-linking
                      here (e.g. via CaptureHome's LiveModeCard, which also
                      hides the entry point itself) gets redirected to the
                      dashboard instead of a dead-end blank screen. The
                      redirect alone can't win every race on a cold load,
                      though — useLiveSession's auto-connect timer also
                      gates connect() on a settled, non-read-only role
                      (see useLiveSession.ts) so a guest's session can never
                      dial in even if this component renders first. */}
                      <Route
                        path={ROUTES.liveSession}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <WriterRoute>
                                <ErrorBoundary level="route">
                                  <LiveSessionPage />
                                </ErrorBoundary>
                              </WriterRoute>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Internal LiveKit call preview. The page itself gates on
                        the `live-call-preview-enabled` flag (NotFound when off),
                        so a shared URL opens nothing for a user outside the
                        rollout. A call writes nothing to the workspace, so no
                        WriterRoute. */}
                      <Route
                        path={ROUTES.liveCall}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <LiveCallPage />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Main application routes */}
                      <Route
                        path={ROUTES.dashboard}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <Dashboard />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />

                      {/* Resource detail routes */}
                      <Route
                        path={ROUTES.collections}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <WorkspacePageForShell />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.artifacts}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <LegacyWorkspaceListRedirect />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.captureStream}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <CaptureStreamRedirect />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.protocols}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <ProtocolLibraryGridPage />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      {/* One run's record. */}
                      <Route
                        path={ROUTES.protocolRun.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <ProtocolRunPage />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      {/* The structured protocol page. Deliberately NOT flag-gated:
                        the flag gates CREATION, and whether this renders
                        structured or legacy is decided by whether the protocol
                        has a version row. A protocol structured while the flag
                        was on must stay readable after it is turned off. */}
                      <Route
                        path={ROUTES.protocol.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <ProtocolDetail />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.equipment}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <LegacyWorkspaceListRedirect />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.equipmentDetail.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <EquipmentDetail />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.graph}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <Graph />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.collection.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <WorkspacePageForShell />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.collectionSingular.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <WorkspacePageForShell />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      {/* Backward-compatible redirects */}
                      <Route
                        path={ROUTES.projects}
                        element={<Navigate to={ROUTES.collections} replace />}
                      />
                      <Route
                        path={ROUTES.projectsLegacy.pattern}
                        element={<ProjectsLegacyRedirect />}
                      />
                      <Route path={ROUTES.projectLegacy.pattern} element={<ProjectRedirect />} />
                      <Route path={ROUTES.samplesLegacy.pattern} element={<SampleRedirect />} />
                      <Route
                        path={ROUTES.note.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <NoteDetail />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.attachment.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <AttachmentDetail />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.spreadsheet.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <SpreadsheetDetail />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.link.pattern}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <ErrorBoundary level="route">
                                <LinkRedirect />
                              </ErrorBoundary>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Trash route — viewers redirected away (no
                      meaningful read-only mode for restoring/purging). */}
                      <Route
                        path={ROUTES.trash}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <WriterRoute>
                                <ErrorBoundary level="route">
                                  <TrashGridPage />
                                </ErrorBoundary>
                              </WriterRoute>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Audit route — admin-style observability; hidden
                      from viewers. */}
                      <Route
                        path={ROUTES.audit}
                        element={
                          <ProtectedRoute>
                            <WorkspaceRoute>
                              <WriterRoute>
                                <ErrorBoundary level="route">
                                  <Audit />
                                </ErrorBoundary>
                              </WriterRoute>
                            </WorkspaceRoute>
                          </ProtectedRoute>
                        }
                      />

                      {/* Settings and profile routes */}
                      <Route
                        path={ROUTES.settings}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <AccountSettings />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.account}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <AccountSettings />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path={ROUTES.admin}
                        element={
                          <ProtectedRoute>
                            <ErrorBoundary level="route">
                              <InternalAdmin />
                            </ErrorBoundary>
                          </ProtectedRoute>
                        }
                      />

                      {/* 404 fallback */}
                      <Route
                        path="*"
                        element={
                          <ErrorBoundary level="route">
                            <NotFound />
                          </ErrorBoundary>
                        }
                      />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </NoWorkspaceGate>
            </Layout>
          </AgentChatProvider>
        </ChatContextProvider>
      </SocketProvider>
    </ErrorBoundary>
  );
}

// Main App wrapper with providers
function App() {
  // `html, body` are `overflow: hidden` but still programmatically scrollable;
  // anything that scrolls them parks the app shell (and the sticky top bar)
  // off-screen with no way back. Mounted at the root so it covers the
  // unauthenticated routes too.
  useDocumentScrollLock();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WorkspaceProvider>
          <UIProvider>
            {/* AiQuotaWarningProvider sits inside the auth provider tree
                but wraps AppRouter so the data hook fires only after the
                router renders. The hook's own `enabled` guard prevents
                requests until workspaceId + auth token are both ready —
                if the auth flow ever changes ordering, that guard is
                what keeps this safe (not the mount position). */}
            <AiQuotaWarningProvider>
              <AppRouter />
            </AiQuotaWarningProvider>
            <CookieConsent />
            <UpgradePromptModal />
            <UploadTray />
            <ViewerForbiddenToastBridge />
          </UIProvider>
        </WorkspaceProvider>
      </AuthProvider>
      {FEATURES.devTools && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}

// Router component that handles authentication routing
export function AppRouter() {
  const { user, bootLoading } = useAuth();
  const location = useLocation();

  // Expire unsaved note recordings left on this device (PHI) on every load,
  // signed in or not: the recovery prompt only runs for a signed-in user, and
  // a shared browser nobody signs in to again must still lose the audio.
  useEffect(() => {
    sweepRecordings().catch(() => undefined);
  }, []);

  // Warm the hot route chunks during idle time once the user is signed in.
  // Production profiling showed every first navigation to a page paid a
  // 0.5–1.4s parallel fetch of that route's lazy chunks; preloading during
  // idle hides that entirely behind user think-time. Deliberately excludes
  // the heavyweight rarely-first-visited chunks (Graph/three.js) and the
  // auth/onboarding pages. Do not schedule the desktop flood while the
  // onboarding route itself is active either: its first render is a critical
  // path, not idle bandwidth. Fire-and-forget — failures are swallowed by
  // preload() and the real navigation retries.
  //
  // Skip entirely on the mobile-only shell ('/m/*'): those routes never
  // navigate into these desktop list/detail pages, so this was pure waste —
  // worse, on the quick-capture "hero moment" screen it was pulling in the
  // tiptap (~1.3MB) and excalidraw (~4.7MB) chunks (transitively, via
  // NoteDetail/AttachmentDetail) on a throttled mobile connection, directly
  // competing with the page's own bundle+font parse for main-thread time and
  // regressing its LCP. Regular desktop routes that happen to also serve
  // mobile viewports (e.g. the unified '/notes', '/collections' detail
  // pages) are NOT excluded — this is a shell check, not a viewport check.
  //
  // Nor on a phone that is about to leave a desktop path: `/` or /dashboard
  // opened on a phone renders here before ProtectedRoute's redirect to
  // /m/capture lands, so the flood fired anyway. A 4x-throttled staging run
  // measured it holding the capture page back to about 8 s (2026-10-28).
  useEffect(() => {
    if (
      bootLoading ||
      !user ||
      location.pathname === ROUTES.onboarding ||
      location.pathname.startsWith('/m/') ||
      (isPhoneViewport() && !isMobileSupportedPath(location.pathname))
    ) {
      return;
    }
    const preloadHotRoutes = () => {
      for (const route of [
        Dashboard,
        NoteDetail,
        AttachmentDetail,
        ProtocolLibraryGridPage,
        WorkspaceCanvasPage,
      ]) {
        route.preload();
      }
    };
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(preloadHotRoutes, { timeout: 5000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(preloadHotRoutes, 3000);
    return () => clearTimeout(id);
  }, [bootLoading, user, location.pathname]);

  // Warm KaTeX's stylesheet during idle time once the user is signed in, so
  // the first formula almost never renders before it arrives
  // (utils/katexStyles.ts). Signed-in only: Login and the other pre-auth pages
  // render no maths, and a public share page asks for the sheet itself when it
  // builds its editor. Unlike the route flood above this also runs on the
  // mobile shell, because mobile Bird renders maths in its replies.
  useEffect(() => {
    if (bootLoading || !user) return;
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(() => void loadKatexStyles(), { timeout: 5000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(() => void loadKatexStyles(), 1000);
    return () => clearTimeout(id);
  }, [bootLoading, user]);

  // GA4 on the credential-entry pages only, so the marketing -> signup funnel
  // is measurable. utils/ga4.ts owns the allowlist and drops anything not on
  // it; sealing on `user` is permanent for the page, which is what stops a
  // sign-out from re-opening capture while authenticated DOM is still painted.
  useEffect(() => {
    if (user) {
      sealGa4ForAuthenticatedSession();
      return;
    }
    if (bootLoading) return;
    trackPreAuthPageView(location.pathname);
  }, [bootLoading, user, location.pathname]);

  if (bootLoading) {
    return <LoadingFallback />;
  }

  // Check if this is an invitation URL (both logged-in and logged-out users)
  const isInvitationUrl = location.pathname === '/auth' && location.search.includes('invitation=');

  // If no user, show auth page or redirect to auth
  if (!user) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path={ROUTES.auth} element={<Login />} />
          <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
          <Route path={ROUTES.authAction} element={<AuthAction />} />
          <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
          <Route path={ROUTES.recoverEmail} element={<RecoverEmail />} />
          <Route path={ROUTES.verifyEmailPrompt} element={<EmailVerificationPrompt />} />
          {/* WorkOS External Sign-in URI — handles auth check internally */}
          <Route path={ROUTES.authWorkos} element={<WorkosSignIn />} />
          <Route path={ROUTES.share.pattern} element={<PublicSharePage />} />
          {/* Magic link landing — unauthenticated device with ?token; redeems and signs in */}
          <Route path={ROUTES.mobileCapture} element={<MagicLinkRedemption />} />
          {/* Connector OAuth return — self-closes the popup / bridges back.
              Present in the unauthenticated tree too so it always resolves
              even if the popup's session hasn't hydrated yet. */}
          <Route path={ROUTES.connectorCallback} element={<ConnectorOAuthCallback />} />
          <Route path={ROUTES.login} element={<NavigateWithSearch to={ROUTES.auth} replace />} />
          <Route path="*" element={<NavigateWithSearch to={ROUTES.auth} replace />} />
        </Routes>
      </Suspense>
    );
  }

  // If user exists, handle invitation or show authenticated app
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Firebase auth action handler - must be accessible to both authenticated and unauthenticated users */}
        <Route path={ROUTES.authAction} element={<AuthAction />} />

        {/* Email verification prompt - accessible to authenticated but unverified users */}
        <Route path={ROUTES.verifyEmailPrompt} element={<EmailVerificationPrompt />} />

        {/* Email verification handler (from email link) */}
        <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />

        {/* Email recovery handler — revert an email change from the security email */}
        <Route path={ROUTES.recoverEmail} element={<RecoverEmail />} />

        {/* WorkOS External Sign-in URI — handles auth check internally */}
        <Route path={ROUTES.authWorkos} element={<WorkosSignIn />} />

        {/* Always handle invitation URLs, even for logged-in users */}
        <Route
          path={ROUTES.auth}
          element={isInvitationUrl ? <AuthRedirect /> : <PostLoginRedirect />}
        />
        {/* Email verification prompt - accessible when logged in but not verified */}
        <Route path={ROUTES.verifyEmailPrompt} element={<EmailVerificationPrompt />} />
        {/* Email verification handler - processes verification links */}
        <Route path={ROUTES.verifyEmail} element={<VerifyEmail />} />
        <Route path={ROUTES.login} element={<NavigateWithSearch to={ROUTES.dashboard} replace />} />
        <Route path={ROUTES.share.pattern} element={<PublicSharePage />} />
        {/* Connector OAuth return — rendered here (not inside AuthenticatedApp)
            so a not-yet-onboarded user isn't bounced to /onboarding before the
            popup can postMessage its outcome and self-close. */}
        <Route path={ROUTES.connectorCallback} element={<ConnectorOAuthCallback />} />
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </Suspense>
  );
}

export default App;
